/**
 * Service history (hoolduspäevik) for the admin dashboard.
 *
 * The specialist writes entries from the mechanic page; the admin reads them
 * here — per equipment in the detail modal, and in bulk for the Hooldusraamat
 * export.
 */

import { t } from '../i18n.js'
import { escapeHtml, formatDate } from '../utils.js'

function refreshIcons() {
  if (typeof lucide !== 'undefined') lucide.createIcons()
}

// PostgREST sends `.in()` filters in the query string, so a single request
// covering hundreds of UUIDs would blow past the URL length limit. Fetch in
// chunks and merge — an EVS 807 book for a large building hits this easily.
const ID_CHUNK_SIZE = 100

/**
 * Fetch service entries for one or more pieces of equipment.
 * Returns them newest first, with the specialist's name resolved.
 *
 * @param {string[]} equipmentIds
 * @returns {Promise<Array>}
 */
export async function fetchServiceHistory(equipmentIds) {
  if (!equipmentIds || equipmentIds.length === 0) return []

  const { supabase } = await import('./main.js')

  const chunks = []
  for (let i = 0; i < equipmentIds.length; i += ID_CHUNK_SIZE) {
    chunks.push(equipmentIds.slice(i, i + ID_CHUNK_SIZE))
  }

  const results = await Promise.all(chunks.map(chunk =>
    supabase
      .from('service_history')
      .select('*, mechanic:users!service_history_mechanic_id_fkey (full_name, username)')
      .in('equipment_id', chunk)
      .order('work_date', { ascending: false })
  ))

  const failed = results.find(r => r.error)
  if (failed) throw failed.error

  // Chunks are each sorted, but the merged list needs one global sort.
  return results
    .flatMap(r => r.data || [])
    .sort((a, b) => new Date(b.work_date) - new Date(a.work_date))
}

/** Specialist display name for one entry, falling back through the options. */
export function specialistName(entry) {
  return entry.mechanic_name
    || entry.mechanic?.full_name
    || entry.mechanic?.username
    || '-'
}

/** Flatten `work_items` (JSONB array of {name, hours}) into one readable line. */
export function workItemsText(entry) {
  const items = entry.work_items || []
  if (items.length === 0) return entry.description || ''

  return items
    .map(item => (item.hours ? `${item.name} (${item.hours} h)` : item.name))
    .join('; ')
}

/** Total hours logged in one entry. */
export function totalHours(entry) {
  return (entry.work_items || []).reduce((sum, item) => sum + (Number(item.hours) || 0), 0)
}

// ==================== DETAIL MODAL ====================

/**
 * Render the service history for one piece of equipment into the
 * admin's equipment detail modal.
 */
export async function loadEquipmentServiceHistory(equipmentId) {
  const container = document.getElementById('detailServiceHistory')
  if (!container) return

  container.innerHTML = `<p class="loading">${escapeHtml(t('common.loading'))}</p>`

  try {
    const entries = await fetchServiceHistory([equipmentId])

    if (entries.length === 0) {
      container.innerHTML = `<p class="text-muted">${escapeHtml(t('serviceHistory.empty'))}</p>`
      return
    }

    container.innerHTML = `
      <div class="service-history-list">
        ${entries.map(renderEntry).join('')}
      </div>`

    refreshIcons()
  } catch (error) {
    console.error('Error loading service history:', error)
    container.innerHTML = `<p class="error-message show">${escapeHtml(error.message || t('common.error'))}</p>`
  }
}

function renderEntry(entry) {
  const hours = totalHours(entry)
  const items = workItemsText(entry)

  return `
    <div class="service-history-entry">
      <div class="service-history-entry-header">
        <span class="service-history-date">${escapeHtml(formatDate(entry.work_date))}</span>
        <span class="service-history-mechanic">
          <i data-lucide="user"></i>${escapeHtml(specialistName(entry))}
        </span>
        ${hours > 0 ? `<span class="service-history-hours">${hours} h</span>` : ''}
      </div>
      ${items ? `<p class="service-history-items">${escapeHtml(items)}</p>` : ''}
      <div class="service-history-entry-footer">
        ${entry.completed_checklist_items
          ? `<span class="service-history-meta"><i data-lucide="clipboard-check"></i>${entry.completed_checklist_items}</span>`
          : ''}
        ${entry.engine_hours != null
          ? `<span class="service-history-meta"><i data-lucide="clock"></i>${entry.engine_hours} h</span>`
          : ''}
        ${entry.act_pdf_url
          ? `<a href="${escapeHtml(entry.act_pdf_url)}" target="_blank" rel="noopener" class="service-history-act">
               <i data-lucide="file-text"></i>${escapeHtml(t('serviceHistory.act'))}
             </a>`
          : ''}
      </div>
    </div>`
}
